import * as d3 from 'd3';
import { isEqual } from 'lodash';
import React, { Component } from 'react';

import analyticService from 'services/analytics';
import { dateService } from 'services/date';
import { formatValue } from '../common/utils';

class Graph extends Component {
	constructor(props) {
		super(props);

		this.dateService = dateService;

		// Google Analytics variables
		this.AnalyticService = analyticService;
		this.tooltipsHovered = 0;
		this.tooltipVisited = false; // True when user leaves tooltip

		// Axes variables
		this.x = {
			axis: null, // X axis
			startDate: null,
			endDate: null,
			numTicks: null,
			tickHeight: 86, // Height of x-axis tick lines (for tooltip)
			transform: null, // Transform css values
		};
		this.y = {
			axis: null, // Y axis
			max: null, // Max y value in stats (revenue, clicks, etc)
			ticks: null, // Values for each tick of Y axis
			tickFormat: null,
			transform: null, // Transform css values
		};

		// Graph variables
		this.svg = null;
		this.svgHeight = null;
		this.svgWidth = null;
		this.margin = {left: 50, bottom: 60};
		this.verticalTranslation = -80; // Vertical translation of graph components
		this.valueLine = null; // Line definition for drawing graph
		this.area = null; // Area definition for coloring below the lines
		this.shadow = null; // Area definition for creating shadow effect
		this.minTickWidth = 28; // Minimum width of a tick on X axis

		// Tooltip variables
		this.graphNode = null;
		this.tickNodes = null;
		this.tooltipNode = null;
		this.tooltipMargin = 24; // CSS margin between tick and tooltip
		this.tooltipWidth = 276; // CSS width of tooltip element
		this.tooltipIndex = null; // Current index we are applying tooltip to
		this.tickPositions = []; // Window position of x-axis ticks

		// Helpers
		this.parseDate = d3.timeParse('%Y-%m-%d');
		this.formatDisplayDate = d3.timeFormat('%a, %b %e'); // ex: 'Mon, Apr 12'
		this.formatDisplayDateWithYear = d3.timeFormat('%b %Y');
		this.formatDate = d3.timeFormat('%Y-%m-%d'); // ex: '2017-04-01'
		this.timeout = null;

		this.onResize = this.onResize.bind(this);

		this.state = {
			tooltip: {
				date: null,
				nextDate: null,
				data: [],
			},
		};
	}

	componentDidMount() {
		this.svg = d3.select('.graph');
		this.initializeGraph();
		window.addEventListener('resize', this.onResize);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onResize);
	}

	componentDidUpdate(prevProps) {
		if (!isEqual(prevProps.stats, this.props.stats) ||
			!isEqual(prevProps.chartOptions, this.props.chartOptions) ||
			!isEqual(prevProps.dates, this.props.dates)) {
			this.tooltipsHovered = 0;
			this.tooltipVisited = false;
			this.handleListeners('remove');
			this.initializeGraph();
		}
	}

	initializeGraph() {
		d3.selectAll('.graph *').remove(); // Clear graph

		this.getAxisVariables();
		this.appendAxes();
		this.defineLinesAndArea();
		this.drawLines();
		this.drawDots();
		this.getTickPositions();
		this.handleListeners('add');
	}

	appendAxes() {
		let prevSecondaryUnit; // Used to track previous data-point month or year
		const secondXLabels = []; // Labels for second x-axis
		const granularity = this.props.granularity;
		this.xAxisDistribution = this.getXAxisDistribution(this.x.ticks);

		// Append primary x and y axes
		this.svg.append('g')
			.attr('class', 'axis axis-x')
			.style('transform', this.x.transform)
			.call(d3.axisBottom(this.x.axis)
				.tickValues(this.x.ticks)
				.tickSize(-this.svgHeight + this.x.tickHeight)
				.tickFormat((tick, i) => {
					if (!this.xAxisDistribution.includes(tick)) return null;

					if (granularity === 'daily' || granularity === 'weekly') {
						if (i === 0 || tick.getMonth() !== prevSecondaryUnit) {
							prevSecondaryUnit = tick.getMonth();
							secondXLabels.push(tick);
						} else {
							secondXLabels.push('');
						}
						return d3.timeFormat('%d')(tick);
					} else if (granularity === 'monthly') {
						if (i === 0 || tick.getFullYear() !== prevSecondaryUnit) {
							prevSecondaryUnit = tick.getFullYear();
							secondXLabels.push(tick);
						} else {
							secondXLabels.push('');
						}
						return d3.timeFormat('%b')(tick);
					} else {
						return d3.timeFormat('%Y')(tick);
					}
				}));

		this.svg.append('g')
			.attr('class', 'axis axis-y')
			.style('transform', this.y.transform)
			.call(d3.axisLeft(this.y.axis)
				.tickValues(this.y.ticks)
				.tickFormat((tick) => {
					if (tick === 0) {
						return d3.format(this.props.chartOptions.d3Format)(tick);
					} else {
						return this.y.tickFormat(tick);
					}
				}));

		// Append a second x-axis for month/year labels
		if (granularity !== 'yearly') {
			const secondaryTickFormat = granularity === 'monthly' ?
				d3.timeFormat('%Y') : d3.timeFormat('%b');
			this.svg.append('g')
				.attr('class', 'axis')
				.style('transform', `translate(${this.margin.left}px,
					${this.adjustedHeight + 20}px)`)
				.call(d3.axisBottom(this.x.axis)
					.tickValues(secondXLabels)
					.tickFormat(secondaryTickFormat));
		}

		// Append grid lines
		this.svg.append('g')
			.attr('class', 'grid-lines')
			.style('transform', this.y.transform)
			.style('stroke-width', '1.5px')
			.call(d3.axisLeft(this.y.axis)
				.tickValues(this.y.ticks)
				.tickFormat('')
				.tickSize(-this.svgWidth));
	}

	defineLinesAndArea() {
		this.valueLine = d3.line()
			.x((data) => this.x.axis(data.x))
			.y((data) => this.y.axis(data.y));

		this.area = d3.area()
			.x((data) => this.x.axis(data.x))
			.y0((data) => this.y.axis(data.y))
			.y1((data) => this.y.axis(0));

		this.shadow = d3.area()
			.x((data) => this.x.axis(data.x))
			.y0((data) => this.y.axis(data.y))
			.y1((data) => this.y.axis(data.y - (0.03 * this.y.max)));
	}

	drawLines() {
		this.props.stats.forEach((stat) => {
			// Format the data for the valueline
			const data = stat.data.map((item, index) => {
				return {
					x: this.parseDate(item.event_date),
					y: parseFloat(item[this.props.chartOptions.statKey]),
				};
			});

			// Plot lines and areas
			this.svg.append('path')
				.datum(data)
				.attr('class', `line ${stat.color}`)
				.style('transform', this.y.transform)
				.attr('d', this.valueLine);
			this.svg.append('path')
				.datum(data)
				.attr('class', `area ${stat.color}`)
				.style('transform', this.y.transform)
				.attr('d', this.area);
			this.svg.append('path')
				.datum(data)
				.attr('class', `area-shadow ${stat.color}`)
				.style('transform', this.y.transform)
				.attr('d', this.shadow);
		});
	}

	drawDots() {
		/*
		 * Draw a dot if start or end date of stats comes after/before the
		 * start or end of our date range
		 */
		const data = this.props.stats.reduce((result, stat) => {
			const firstStat = stat.data[0];
			const lastStat = stat.data.slice(-1)[0];
			const addDotToData = (dataPoint) => {
				result.push({
					x: this.parseDate(dataPoint.event_date),
					y: parseFloat(dataPoint[this.props.chartOptions.statKey]),
					color: stat.color,
				});
			};

			if (this.dateService.compareDates(this.x.startDate, firstStat.event_date) < 0) {
				addDotToData(firstStat);
			}
			if (this.dateService.compareDates(this.x.endDate, lastStat.event_date) > 0) {
				addDotToData(lastStat);
			}
			return result;
		}, []);

		if (!data.length) return;

		this.svg.selectAll('dot')
			.data(data)
			.enter().append('circle')
			.attr('class', (d) => `dot ${d.color}`)
			.attr('cx', (d) => this.x.axis(d.x))
			.attr('cy', (d) => this.y.axis(d.y))
			.attr('r', 3)
			.style('transform', this.y.transform);
	}

	onResize() {
		// Resize graph to 100% of screen width, once user stops adjusting width
		if (this.timeout) window.clearTimeout(this.timeout);
		this.timeout = window.setTimeout(() => {
			this.initializeGraph();
		}, 200);
	}

	/* Graph Helpers */
	getAxisVariables() {
		this.y.tickFormat = d3.format(`${this.props.chartOptions.d3Format}.0f`); // Ex: $200 or 200
		this.y.max = this.getYMax();
		this.y.ticks = [0, 1, 2, 3].map((val) => {
			const rawTickValue = this.y.max / 3 * val;
			return this.y.max > 1 ? Math.round(rawTickValue) : +rawTickValue.toFixed(2);
		});

		this.x.startDate = this.parseDate(this.props.dates.date_from);
		this.x.ticks = this.props.xTicks;

		// For larger granularities, set end of x axis to the last calculated tick
		if (this.props.granularity !== 'daily') {
			this.x.endDate = this.x.ticks[this.x.ticks.length - 1];
		} else {
			this.x.endDate = this.parseDate(this.props.dates.date_to);
		}

		// Define x and y axis transform values
		this.svgHeight = parseInt(this.svg.style('height'));
		this.svgWidth = parseInt(this.svg.style('width'));

		this.adjustedHeight = this.svgHeight - this.margin.bottom;
		this.x.transform = `translate(${this.margin.left}px,
			${this.adjustedHeight}px)`;
		this.y.transform = `translate(${this.margin.left}px, ${this.verticalTranslation}px)`;

		// Define x and y axis
		this.x.axis = d3.scaleTime()
			.range([0, this.svgWidth - this.margin.left - 10])
			.domain([this.x.startDate, this.x.endDate]);

		this.y.axis = d3.scaleLinear()
			.range([this.svgHeight + 20, 0])
			.domain([0, this.y.max * 1.5]);
	}

	getYMax() {
		const max = d3.max(
			this.props.stats
				.map((obj) => obj.data)
				.map((arr) => {
					const values = arr.map((obj) => {
						return parseFloat(obj[this.props.chartOptions.statKey]);
					});
					return d3.max(values);
				})
		);
		if (max >= 1000) {
			this.y.tickFormat = d3.format(`${this.props.chartOptions.d3Format}.2s`); // Ex: $1k
		} else if (max <= 1) {
			this.y.tickFormat = d3.format(`${this.props.chartOptions.d3Format}.2f`); // Ex: $0.12
		}
		return max;
	}

	getXAxisDistribution(ticks) {
		// Use recursion to cut out half the ticks until we are under maxTicks
		const maxTicks = Math.round(this.svgWidth / this.minTickWidth);

		// Return ticks if maxTicks is NaN (occasionally happens during integration tests)
		if (ticks.length <= maxTicks || Number.isNaN(maxTicks)) return ticks;

		// Never remove the first tick from the axis
		const distribution = ticks.slice(1).reduce((agg, tick, i) => {
			if (i % 2 !== 0) agg.push(tick);
			return agg;
		}, []);
		distribution.unshift(ticks[0]);

		return this.getXAxisDistribution(distribution);
	}

	/* Tooltip Helpers */
	getTickPositions() {
		this.tickPositions = [];
		this.tickNodes = document.querySelectorAll('.graph .axis-x .tick');
		Array.from(this.tickNodes).forEach((tick, i) => {
			const hiddenTick = !this.xAxisDistribution.includes(this.x.ticks[i]);
			const { left, width } = tick.getBoundingClientRect();
			const xPosition = hiddenTick ? left : left + (width / 2);

			this.tickPositions.push(xPosition);
		});
	}

	getTooltipPosition(event) {
		let index = null;
		let minDiff = null;

		// Find closest tick to the mouse position
		this.tickPositions.forEach((pos, i) => {
			const diff = Math.abs(pos - event.x);
			if (!minDiff || diff < minDiff) {
				minDiff = diff;
				index = i;
			}
		});

		if (index === this.tooltipIndex) return;

		this.tooltipIndex = index;
		this.updateTooltipState();
	}

	moveTooltip() {
		const tooltipTop = this.graphNode.getBoundingClientRect().top + 50;
		const tickPosition = this.tickPositions[this.tooltipIndex];
		let tooltipLeft = tickPosition + this.tooltipMargin;
		const widerThanScreen = window.innerWidth < tooltipLeft + this.tooltipWidth;

		if (widerThanScreen) {
			tooltipLeft = tickPosition - (this.tooltipMargin / 3) - this.tooltipWidth;
		}

		// Move tooltip
		Object.assign(this.tooltipNode.style, {
			display: 'block',
			top: `${tooltipTop}px`,
			left: `${tooltipLeft}px`,
		});

		// Increment if tooltip has data
		if (this.state.tooltip.data.length) {
			this.tooltipsHovered++;
		}

		// Highlight proper tick on x-axis
		Array.from(this.tickNodes).forEach((node, i) => {
			if (i === this.tooltipIndex) {
				node.classList.add('hover');
			} else {
				node.classList.remove('hover');
			}
		});
	}

	getTooltipDisplayDate(date, nextDate) {
		const granularity = this.props.granularity;
		const displayFunction = granularity === 'yearly' ?
			this.formatDisplayDateWithYear : this.formatDisplayDate;

		if (granularity === 'daily') {
			return this.formatDisplayDate(date);
		}

		if (!nextDate) {
			return `${displayFunction(date)} - Today`;
		}

		return `${displayFunction(date)} - ${displayFunction(nextDate)}`;
	}

	updateTooltipState() {
		const tooltipData = [];
		const date = this.x.ticks[this.tooltipIndex];
		let nextDate = this.x.ticks[this.tooltipIndex + 1];

		/*
		 * Subtract one day from the tick, so the end of the date range
		 * range does not overlap with the next tick
		 */
		if (nextDate) {
			nextDate = this.dateService.offsetFromDate(nextDate, -1);
		}

		this.props.stats.forEach((stat) => {
			const data = stat.data.find((item) => item.event_date === this.formatDate(date));
			if (data) {
				tooltipData.push({
					name: stat.name,
					color: stat.color,
					...data,
				});
			}
		});

		// User hovered from tick with tooltip to tick with no data
		if (this.state.tooltip.data.length && !tooltipData.length) {
			this.tooltipVisited = true;
		}

		this.setState({tooltip: {
			date: date,
			nextDate: nextDate,
			data: tooltipData,
		}}, () => {
			if (tooltipData.length) {
				this.moveTooltip();
			} else {
				this.hideTooltip();
			}
		});
	}

	hideTooltip(event) {
		const {bottom, left, top} = this.graphNode.getBoundingClientRect();
		if (event && event.clientY < bottom &&
			event.clientY > top && event.clientX > left) {
			return;
		}

		/*
		 * Send hover count if user leaves graph and last hovered x tick had a tooltip,
		 * or if user moves from a tick with tooltip to a tick with no tooltip.
		 */
		if ((this.state.tooltip.data.length && this.tooltipsHovered !== 0) || this.tooltipVisited) {
			const gaLabel = `Graph Visualization ${this.tooltipsHovered} Tooltips Viewed`;
			this.AnalyticService.trackEvent('Mega Tables', 'Hover', gaLabel);
			this.tooltipsHovered = 0;
			this.tooltipVisited = false;
		}

		this.tooltipIndex = null;
		this.tooltipNode.style.display = 'none';
		Array.from(this.tickNodes).forEach((node) => {
			node.classList.remove('hover');
		});
	}

	handleListeners(action) {
		const method = action === 'add' ? 'addEventListener' : 'removeEventListener';
		if (this.graphNode) {
			this.graphNode[method]('mousemove', this.getTooltipPosition.bind(this));
			this.graphNode[method]('mouseleave', this.hideTooltip.bind(this));
		}
		if (this.tooltipNode) {
			this.tooltipNode[method]('mousemove', this.getTooltipPosition.bind(this));
		}
	}

	render() {
		const chartOptions = this.props.chartOptions;

		const tooltipRows = this.state.tooltip.data.map((data, i) => {
			const value = data[chartOptions.statKey];
			return (
				<div key={i} className="row">
					<span className="article">{data.name}</span>
					<span className={`tooltip-stat ${data.color}`}>
						{formatValue(value, chartOptions.dataType)}
					</span>
				</div>
			);
		});

		return (
			<>
				<svg ref={(node) => this.graphNode = node} className="graph"></svg>
				<div ref={(node) => this.tooltipNode = node} className="graph-tooltip">
					<div className="date">
						{this.getTooltipDisplayDate(
							this.state.tooltip.date, this.state.tooltip.nextDate
						)}
					</div>
					{tooltipRows}
				</div>
			</>
		);
	}
}

export default Graph;
